import { useState } from "react";
import { CalendarCheck, StickyNote, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../lib/api";
import { fmtDate, fmtDateTime, fmtMoney, todayAthens } from "../../../lib/format";
import { errMsg } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Panel, btnPrimary, chip, input } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { OrdersList } from "../../../components/common/OrdersList";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { useConfirm } from "../../../components/common/ConfirmDialog";
import { hasPerm } from "../../../lib/perm";

const td = "px-3 py-2.5 text-sm";

export const StatementTab = ({ code }) => {
  const { t } = useI18n();
  const r = useApi("staffStatement", "/finance/statement", { customer_code: code }, { staleTime: 0 });
  if (r.isLoading) return <SkeletonBlock className="h-64" />;
  const s = r.data;
  return (
    <Panel title={`${fmtDate(s.date_from)} – ${fmtDate(s.date_to)}`} sub={`${t("x.cust.opening")}: ${fmtMoney(s.opening_balance)}`} testId="customer-statement">
      {!s.transactions.length ? <p className="text-sm text-slate-500">{t("x.cust.noTx")}</p> : (
        <div className="-mx-4 overflow-x-auto sm:mx-0">
          <table className="w-full min-w-[560px]"><thead><tr className="text-left text-xs text-slate-500">
            <th className={td}>{t("x.cust.date")}</th><th className={td}>{t("x.cust.doc")}</th><th className={`${td} text-right`}>{t("x.cust.debit")}</th><th className={`${td} text-right`}>{t("x.cust.credit")}</th><th className={`${td} text-right`}>{t("x.cust.running")}</th></tr></thead>
            <tbody className="divide-y divide-slate-100">{[...s.transactions].reverse().map((x, i) => (
              <tr key={i} className="tabular-nums text-navy"><td className={td}>{fmtDate(x.date)}</td><td className={td}>{x.document}</td>
                <td className={`${td} text-right`}>{x.debit ? fmtMoney(x.debit) : ""}</td><td className={`${td} text-right`}>{x.credit ? fmtMoney(x.credit) : ""}</td><td className={`${td} text-right font-medium`}>{fmtMoney(x.running_balance)}</td></tr>))}
            </tbody></table>
        </div>)}
    </Panel>
  );
};

export const OrdersTab = ({ customer }) => {
  const { t } = useI18n();
  const r = useApi("staffCustOrders", "/orders", { customer_code: customer.code }, { staleTime: 0 });
  if (r.isLoading) return <SkeletonBlock className="h-64" />;
  if (!r.data.length) return <p className="text-sm text-slate-500">{t("x.cust.noOrders")}</p>;
  return <div className="rounded-2xl bg-white px-5 py-2 shadow-sm"><OrdersList orders={r.data.slice(0, 50)} customersByCode={{ [customer.code]: customer }} testId="customer-orders" /></div>;
};

export const PaymentsTab = ({ code }) => {
  const { t } = useI18n();
  const r = useApi("staffPayments", "/finance/payments", { customer_code: code }, { staleTime: 0 });
  if (r.isLoading) return <SkeletonBlock className="h-40" />;
  if (!r.data.length) return <p className="text-sm text-slate-500">{t("x.cust.noPayments")}</p>;
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm" data-testid="customer-payments">
      {r.data.map((p) => (
        <li key={`${p.document}-${p.date}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
          <div><p className="font-medium text-navy">{fmtDate(p.date)} · {p.document}</p><p className="text-xs text-slate-500">{t(`account.method.${p.method}`) || p.method} · {p.reference}</p></div>
          <span className="font-semibold tabular-nums text-brand">{fmtMoney(p.amount)}</span>
        </li>))}
    </ul>
  );
};

const AddUserRow = ({ code, onChange }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ email: "", name: "", role: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users/invite", { ...f, customer_code: code, role: f.role || undefined });
      toast.success(t("users.invited"));
      setF({ email: "", name: "", role: "" });
      onChange();
    } catch (err) { toast.error(errMsg(t, err)); } finally { setBusy(false); }
  };
  const cls = "mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-navy";
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end" data-testid="customer-add-user">
      <label className="block text-xs text-slate-500">{t("login.email")}<input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} data-testid="customer-add-user-email" /></label>
      <label className="block text-xs text-slate-500">{t("users.name")}<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="customer-add-user-name" /></label>
      <label className="block text-xs text-slate-500">{t("users.role")}
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={cls} data-testid="customer-add-user-role">
          <option value="">{t("users.roleAuto")}</option><option value="approver">{t("users.roles.approver")}</option><option value="buyer">{t("users.roles.buyer")}</option>
        </select>
      </label>
      <button type="submit" disabled={busy} className="flex h-10 items-center gap-2 rounded-full bg-navy px-4 text-sm font-semibold text-white disabled:opacity-50" data-testid="customer-add-user-submit"><UserPlus className="h-4 w-4" />{t("users.invite")}</button>
    </form>
  );
};

export const UsersTab = ({ code, users, onChange }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const confirm = useConfirm();
  const can = hasPerm(user, "customers:status");
  const canManage = hasPerm(user, "customer_users:manage");
  const remove = async (u) => {
    const ok = await confirm({ title: t("x.cust.removeUser", { name: u.name }), danger: true, confirmLabel: t("x.cust.remove") });
    if (ok === false) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success(t("x.cust.removed")); onChange(); } catch (e) { toast.error(errMsg(t, e)); }
  };
  const toggle = async (u) => {
    const reason = await confirm({ title: t(u.active ? "cstat.userDeact" : "cstat.userAct", { name: u.name }), danger: u.active,
      confirmLabel: t(u.active ? "cstat.deactivate" : "cstat.activate"), input: { label: t("cstat.reason"), minLength: 0 } });
    if (reason === false) return;
    try { await api.post(`/staff/customers/${encodeURIComponent(code)}/users/${u.id}/active`, { active: !u.active, reason: reason || null }); onChange(); }
    catch (e) { toast.error(errMsg(t, e)); }
  };
  return (
    <div className="space-y-3">
      {canManage && <AddUserRow code={code} onChange={onChange} />}
      {!users.length ? <p className="text-sm text-slate-500" data-testid="customer-users-empty">{t("x.cust.noUsers")}</p> : (
    <ul className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm" data-testid="customer-users">
      {users.map((u) => (
        <li key={u.email} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
          <div className="min-w-0"><p className="font-medium text-navy">{u.name}</p><p className="truncate text-xs text-slate-500">{u.email} · {u.roles.map((r) => t(`ops.role.${r}`)).join(", ")}</p></div>
          <span className="text-xs text-slate-500" data-testid={`customer-user-state-${u.id}`}>{!u.active ? t("x.cust.inactive") : u.invite_cancelled ? t("cstat.cancelled") : u.status === "invited" ? t("x.cust.invited") : `${t("x.cust.lastLogin")}: ${u.last_login_at ? fmtDateTime(u.last_login_at) : t("x.cust.never")}`}</span>
          <div className="flex gap-2">
            {can && <button type="button" onClick={() => toggle(u)} data-testid={`customer-user-toggle-${u.id}`} className={`h-9 rounded-full px-3 text-xs font-semibold ${u.active ? "bg-rose-50 text-rose-700" : "bg-brand/20 text-navy"}`}>{t(u.active ? "cstat.deactivate" : "cstat.activate")}</button>}
            {canManage && u.status === "invited" && <button type="button" onClick={() => remove(u)} data-testid={`customer-user-remove-${u.id}`} className="h-9 rounded-full border border-rose-200 px-3 text-xs font-semibold text-rose-700">{t("x.cust.remove")}</button>}
          </div>
        </li>))}
    </ul>)}
    </div>
  );
};

export const NotesTab = ({ code, notes, onChange }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [f, setF] = useState({ kind: "note", text: "", visit_date: todayAthens() });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/customers/${encodeURIComponent(code)}/notes`, { kind: f.kind, text: f.text, visit_date: f.kind === "visit" ? f.visit_date : null });
      setF((s) => ({ ...s, text: "" })); onChange(); toast.success(t("x.saved"));
    } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  const del = async (n) => {
    if (!(await confirm({ title: t("x.delete"), body: n.text.slice(0, 80), danger: true, confirmLabel: t("x.delete") }))) return;
    try { await api.delete(`/staff/customers/${encodeURIComponent(code)}/notes/${n.id}`); onChange(); } catch (e) { toast.error(errMsg(t, e)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
      <Panel title={t("x.cust.notes.add")} sub={t("x.cust.notes.internal")} testId="note-form">
        <div className="mb-3 flex gap-2">{["note", "visit"].map((k) => <button key={k} type="button" className={chip(f.kind === k)} onClick={() => setF((s) => ({ ...s, kind: k }))} data-testid={`note-kind-${k}`}>{t(`x.cust.notes.${k}`)}</button>)}</div>
        {f.kind === "visit" && <div className="mb-3"><DateField value={f.visit_date} onChange={(v) => setF((s) => ({ ...s, visit_date: v }))} testId="note-visit-date" /></div>}
        <textarea rows={4} value={f.text} onChange={(e) => setF((s) => ({ ...s, text: e.target.value }))} placeholder={t("x.cust.notes.placeholder")} className={`${input} h-auto py-2`} data-testid="note-text" />
        <button type="button" disabled={busy || f.text.trim().length < 2} onClick={save} className={`${btnPrimary} mt-3 w-full`} data-testid="note-save">{t("x.save")}</button>
      </Panel>
      {!notes.length ? <EmptyState icon={StickyNote} title={t("x.cust.notes.empty")} testId="notes-empty" /> : (
        <ul className="space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <li key={n.id} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="flex items-center gap-2 text-xs font-medium text-slate-500">
                  {n.kind === "visit" ? <CalendarCheck className="h-4 w-4 text-brand" /> : <StickyNote className="h-4 w-4 text-navy" />}
                  {n.kind === "visit" ? `${t("x.cust.notes.visit")} ${fmtDate(n.visit_date)}` : t("x.cust.notes.note")} · {n.by_name} · {fmtDateTime(n.at)}
                </p>
                {(n.by === user.email || user.permissions.includes("*")) && <button type="button" onClick={() => del(n)} aria-label="delete" className="grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-navy">{n.text}</p>
            </li>))}
        </ul>)}
    </div>
  );
};
